import { createContext, useContext } from "react";

import type { LoginFlowComponents, LoginFlowProviderProps } from "./types";

/**
 Dependency-inversion seam between the shell (session infrastructure) and the
 login UX flows (owned by the common-services module).

 Shell components (`AuthGate`, `CheckUserSession`) decide when a login,
 re-auth or IdP discovery screen must be shown, but they must not import
 domain-module code directly (module boundary rule: shell never imports from
 domain modules). Instead, the composition root (`src/main.tsx`) registers
 the concrete login-flow components via `LoginFlowProvider`.

 The registry contract and component prop types live in `./types`.

 See `common-services/features/login/AUTH.md` for the full auth architecture.
 */

const LoginFlowContext = createContext<LoginFlowComponents | null>(null);

export const LoginFlowProvider = ({
  components,
  children,
}: LoginFlowProviderProps) => (
  <LoginFlowContext.Provider value={components}>
    {children}
  </LoginFlowContext.Provider>
);

export const useLoginFlows = (): LoginFlowComponents => {
  const components = useContext(LoginFlowContext);
  if (!components) {
    throw new Error(
      "useLoginFlows: no LoginFlowProvider found in the component tree. " +
        "Register the login-flow components at the composition root (src/main.tsx).",
    );
  }
  return components;
};
